// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { api } from "@/api/client";
import { type ImportEntry, useImportsListQuery } from "@/api/hooks/use-imports";
import { useStoresQuery } from "@/api/hooks/use-stores";
import { ImportPlusIcon } from "@/components/icons";
import { PageHeader } from "@/components/layout/page-header";
import { FilterPresets } from "@/components/shared/filter-presets";
import { RequirePermission } from "@/components/shared/require-permission";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ImportDataDialog } from "@/features/imports/import-data-dialog";
import { ImportsTable } from "@/features/imports/imports-table";
import { ImportsToolsMenu } from "@/features/imports/imports-tools-menu";
import { ManualSftpImportDialog } from "@/features/imports/manual-sftp-import-dialog";
import { IMPORTS_FILTER_DEFAULTS, useImportsFiltersState } from "@/features/imports/use-imports-filters-state";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { downloadCsv } from "@/lib/csv";
import { formatDuration, formatIsoDateTime } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/ingress/imports/")({
  validateSearch: z.object({ f: z.string().optional() }),
  component: () => (
    <RequirePermission permission={PAGE_PERMISSIONS.ingressImports}>
      <IngressImportsPage />
    </RequirePermission>
  ),
});

function IngressImportsPage() {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { f: filtersParam } = Route.useSearch();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.ingressImportsEdit);

  const [importDataOpen, setImportDataOpen] = useState(false);
  const [manualSftpOpen, setManualSftpOpen] = useState(false);
  const [chainFilter, setChainFilter] = useState("");

  const { data: ctx } = useBackofficeContext();

  const onSync = useCallback(
    (encoded: string | null) => {
      navigate({
        to: "/ingress/imports",
        search: encoded ? { f: encoded } : {},
        replace: true,
      });
    },
    [navigate],
  );

  const filters = useImportsFiltersState(filtersParam, onSync);
  const {
    currentFilters,
    applyPreset,
    pagination,
    setPagination,
    sorting,
    setSorting,
    search,
    setSearch,
    storeFilter,
    setStoreFilter,
    importTypeFilter,
    setImportTypeFilter,
    sourceFilter,
    setSourceFilter,
    statusFilter,
    setStatusFilter,
    resetToFirstPage,
  } = filters;

  // When a chain is filtered, scope the Store filter dropdown to that chain's stores.
  const { data: chainStoresData } = useStoresQuery({
    chainName: chainFilter || undefined,
    limit: -1,
    onlyActive: false,
    enabled: !!chainFilter,
  });

  const { data, isLoading, refetch, isFetching } = useImportsListQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    sortBy: "id",
    orderBy: "desc",
    search,
    chainName: chainFilter,
    storeName: storeFilter,
    importType: importTypeFilter,
    source: sourceFilter,
    status: statusFilter,
  });

  const chainOptions = useMemo(
    () => (ctx?.chains ?? []).map((c) => ({ chainName: c.value, chainDisplay: c.display })),
    [ctx?.chains],
  );

  const storeOptions = useMemo(() => {
    // When a chain is filtered, scope the dropdown to that chain's full store list.
    if (chainFilter && chainStoresData?.stores) {
      return chainStoresData.stores.map((s) => ({
        storeId: s.storeId,
        storeName: s.storeName,
        storeDisplay: s.legibleName,
      }));
    }
    // No chain filter: source the dropdown from the global backoffice context so users see
    // every store (not just the ones the current imports page happens to surface). Merge in
    // the response's stores so the Store column's storeId → store-name link resolver works
    // for the rows actually rendered.
    const byName = new Map<string, { storeId: number; storeName: string; storeDisplay: string }>();
    for (const s of ctx?.stores ?? []) {
      byName.set(s.value, { storeId: 0, storeName: s.value, storeDisplay: s.display });
    }
    for (const s of data?.stores ?? []) {
      byName.set(s.storeName, {
        storeId: s.storeId,
        storeName: s.storeName,
        storeDisplay: s.storeDisplay,
      });
    }
    // Pill safety net: if the currently-selected store wasn't in either source yet, synthesize it
    // so the FilterSelect pill renders its label immediately on selection.
    if (storeFilter && !byName.has(storeFilter)) {
      const ctxStore = ctx?.stores?.find((s) => s.value === storeFilter);
      byName.set(storeFilter, { storeId: 0, storeName: storeFilter, storeDisplay: ctxStore?.display ?? storeFilter });
    }
    return Array.from(byName.values());
  }, [chainFilter, chainStoresData?.stores, data?.stores, storeFilter, ctx?.stores]);

  const handleExport = useCallback(async () => {
    const res = await api.GET("/v1/ingress/imports", {
      params: {
        query: {
          page: 1,
          limit: -1,
          sortBy: "id",
          orderBy: "desc",
          "filter[chainName]": chainFilter || undefined,
          "filter[storeName]": storeFilter || undefined,
          "filter[importType]": importTypeFilter || undefined,
          "filter[status]": statusFilter || undefined,
          "filter[source]": sourceFilter || undefined,
        },
      },
    });
    const items = (res.data as unknown as { imports: ImportEntry[] }).imports;
    downloadCsv(
      "imports.csv",
      ["ID", "Type", "Store Code", "Source", "Status", "Uploaded By", "Processing Time", "Created"],
      items.map((i) => [
        String(i.importRequestId),
        i.importType,
        i.storeCode ?? "",
        i.source ?? "api",
        i.status ?? "",
        String(i.createdBy ?? ""),
        i.processingTime != null ? formatDuration(i.processingTime) : "",
        formatIsoDateTime(i.createdAt),
      ]),
    );
  }, [chainFilter, storeFilter, importTypeFilter, statusFilter, sourceFilter]);

  return (
    <div>
      <PageHeader title={t("ingressImports.title")} />
      <div className="p-6">
        <ImportsTable
          scope="ingress"
          storageKey="ingress-imports"
          presets={
            <FilterPresets
              storageKey="ingress-imports"
              defaults={IMPORTS_FILTER_DEFAULTS}
              current={currentFilters}
              onApply={applyPreset}
            />
          }
          imports={data?.imports ?? []}
          count={data?.count ?? 0}
          isLoading={isLoading}
          isFetching={isFetching}
          refetch={refetch}
          stores={storeOptions}
          chains={chainOptions}
          users={data?.users}
          vendors={data?.vendors}
          canEdit={canEdit}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            resetToFirstPage();
          }}
          chainFilter={chainFilter}
          onChainFilterChange={(value) => {
            setChainFilter(value);
            // Switching chain invalidates the store choice (different stores per chain).
            setStoreFilter("");
            resetToFirstPage();
          }}
          storeFilter={storeFilter}
          onStoreFilterChange={(value) => {
            setStoreFilter(value);
            resetToFirstPage();
          }}
          importTypeFilter={importTypeFilter}
          onImportTypeFilterChange={(value) => {
            setImportTypeFilter(value);
            resetToFirstPage();
          }}
          sourceFilter={sourceFilter}
          onSourceFilterChange={(value) => {
            setSourceFilter(value);
            resetToFirstPage();
          }}
          statusFilter={statusFilter}
          onStatusFilterChange={(value) => {
            setStatusFilter(value);
            resetToFirstPage();
          }}
          onExport={handleExport}
          toolbar={
            <>
              <ImportsToolsMenu>
                <DropdownMenuItem onSelect={() => setManualSftpOpen(true)}>
                  {t("imports.manualSftp.menuItem")}
                </DropdownMenuItem>
              </ImportsToolsMenu>
              <Button onClick={() => setImportDataOpen(true)} disabled={!canEdit}>
                <ImportPlusIcon className="h-4 w-4" />
                {t("imports.importData")}
              </Button>
            </>
          }
        />
      </div>
      <ImportDataDialog open={importDataOpen} onOpenChange={setImportDataOpen} chainName={chainFilter || undefined} />
      {/* No scope of its own — the chain follows whatever the page is filtered to, and falls back
          to the chain of whichever location the operator picks. */}
      <ManualSftpImportDialog
        open={manualSftpOpen}
        onOpenChange={setManualSftpOpen}
        chainName={chainFilter || undefined}
        canEdit={canEdit}
      />
    </div>
  );
}
